import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Plot from 'react-plotly.js';
import axios from 'axios';

// Análisis de Datos → "Comparativa con el año pasado".
// Gasto por alumno igual que en "Gasto semanal por alumno": por piso y semana, la suma
// de precio_real / nº de alumnos de cada compra; cada semana, media de los pisos de cada
// supervisora y luego media entre supervisoras, solo con las marcadas hoy para la media
// que también estaban el año pasado. Los datos del año pasado vienen de la copia de la BBDD
// (tabla compras_historico).

const AZUL = '#2563eb';   // este año
const GRIS = '#94a3b8';   // año pasado
const CATS = [
  ['basico', 'Básico'], ['proteina', 'Proteína'], ['snacks', 'Snacks'], ['limpieza', 'Limpieza'], ['otros', 'Sin etiquetar'],
];

const eur = (v, dec = 2) => (v === null || v === undefined ? '—' : `${Number(v).toLocaleString('es-ES', { minimumFractionDigits: dec, maximumFractionDigits: dec })} €`);
const num = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('es-ES'));
const ddmm = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');
const ddmmaa = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '');
const pct = (a, b) => (a === null || a === undefined || !b ? null : ((a / b) - 1) * 100);

// Variación: gastar más = rojo, gastar menos = verde (con flecha, no solo color)
const Delta = ({ a, b, invertir = false, sufijo = '' }) => {
  const p = pct(a, b);
  if (p === null) return <span style={{ color: '#94a3b8' }}>—</span>;
  const sube = p > 0.05, baja = p < -0.05;
  const malo = invertir ? baja : sube;
  const color = !sube && !baja ? '#64748b' : (malo ? '#b91c1c' : '#15803d');
  const bg = !sube && !baja ? '#f1f5f9' : (malo ? '#fee2e2' : '#dcfce7');
  return (
    <span style={{ color, background: bg, borderRadius: 999, padding: '2px 9px', fontWeight: 800, fontSize: 13, whiteSpace: 'nowrap' }}>
      {sube ? '▲' : baja ? '▼' : '='} {p > 0 ? '+' : ''}{p.toFixed(1).replace('.', ',')}%{sufijo}
    </span>
  );
};

const Tarjeta = ({ titulo, actual, pasado, fmt, invertir, nota }) => (
  <div style={{ flex: '1 1 190px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '14px 16px', boxShadow: '0 4px 14px rgba(15,23,42,0.04)' }}>
    <div style={{ fontSize: 13, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.3 }}>{titulo}</div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 26, fontWeight: 900, color: '#0f172a' }}>{fmt(actual)}</span>
      <Delta a={actual} b={pasado} invertir={invertir} />
    </div>
    <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Año pasado: <b>{fmt(pasado)}</b></div>
    {nota && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{nota}</div>}
  </div>
);

const Seccion = ({ titulo, subtitulo, children, derecha }) => (
  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 16, marginBottom: 18, boxShadow: '0 4px 14px rgba(15,23,42,0.04)' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>{titulo}</div>
        {subtitulo && <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{subtitulo}</div>}
      </div>
      {derecha}
    </div>
    {children}
  </div>
);

const Segmentos = ({ opciones, valor, onChange }) => (
  <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: 999, padding: 3, gap: 2, flexWrap: 'wrap' }}>
    {opciones.map(([v, t]) => (
      <button key={v} onClick={() => onChange(v)} style={{
        border: 'none', borderRadius: 999, padding: '7px 13px', cursor: 'pointer', fontWeight: 800, fontSize: 13,
        background: valor === v ? '#fff' : 'transparent', color: valor === v ? '#1d4ed8' : '#475569',
        boxShadow: valor === v ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
      }}>{t}</button>
    ))}
  </div>
);

const th = { textAlign: 'left', padding: '8px 10px', fontSize: 12.5, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' };
const td = { padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontSize: 14, color: '#0f172a' };
const tdNum = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

// Tabla ordenable sencilla
function Tabla({ columnas, filas, ordenInicial, max }) {
  const [orden, setOrden] = useState(ordenInicial);
  const ordenadas = useMemo(() => {
    const col = columnas.find((c) => c.k === orden.k);
    const val = col && col.orden ? col.orden : (f) => f[orden.k];
    return [...filas].sort((x, y) => {
      const a = val(x), b = val(y);
      if (a === null || a === undefined) return 1;
      if (b === null || b === undefined) return -1;
      const r = typeof a === 'string' ? a.localeCompare(b) : a - b;
      return orden.asc ? r : -r;
    });
  }, [filas, orden, columnas]);
  const vista = max ? ordenadas.slice(0, max) : ordenadas;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {columnas.map((c) => (
              <th key={c.k} style={{ ...th, textAlign: c.num ? 'right' : 'left' }}
                onClick={() => setOrden((o) => ({ k: c.k, asc: o.k === c.k ? !o.asc : !!c.ascPrimero }))}>
                {c.t}{orden.k === c.k ? (orden.asc ? ' ▲' : ' ▼') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {vista.map((f, i) => (
            <tr key={i}>
              {columnas.map((c) => <td key={c.k} style={c.num ? tdNum : td}>{c.r ? c.r(f) : f[c.k]}</td>)}
            </tr>
          ))}
          {vista.length === 0 && <tr><td colSpan={columnas.length} style={{ ...td, color: '#94a3b8', textAlign: 'center' }}>Sin datos</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function ComparativaAnioPasado() {
  const [filtros, setFiltros] = useState({ desde: '', hasta: '', modo: 'curso', rutas: '1,2' });
  const [aplicados, setAplicados] = useState(filtros);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [vistaPrecios, setVistaPrecios] = useState('mas_comprados');
  const [minSemanas, setMinSemanas] = useState(3);
  const [buscaPiso, setBuscaPiso] = useState('');
  const [todosPisos, setTodosPisos] = useState(false);

  const cargar = useCallback(async (f) => {
    setCargando(true); setError(null);
    try {
      const params = { modo: f.modo, rutas: f.rutas };
      if (f.desde) params.desde = f.desde;
      if (f.hasta) params.hasta = f.hasta;
      const r = await axios.get('/api/analisis/comparativa', { params, withCredentials: true });
      setDatos(r.data);
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.error) || 'No se ha podido cargar la comparativa');
    } finally {
      setCargando(false);
    }
  }, []);
  useEffect(() => { cargar(aplicados); }, [aplicados, cargar]);

  const aplicar = (cambios) => {
    const f = { ...filtros, ...cambios };
    setFiltros(f);
    setAplicados(f);
  };

  const k = datos && datos.kpis;
  const semanas = (datos && datos.semanas) || [];
  const etiquetaSemana = (s) => (aplicados.modo === 'curso' ? `Sem ${s.semana_curso} · ${ddmm(s.semana)}` : ddmm(s.semana));

  const pisos = useMemo(() => {
    if (!datos) return [];
    const q = buscaPiso.trim().toLowerCase();
    return datos.pisos.filter((p) => p.dif !== null && p.semanas_actual >= minSemanas && p.semanas_pasado >= minSemanas
      && (!q || `${p.abreviatura || ''} ${p.supervisora || ''}`.toLowerCase().includes(q)));
  }, [datos, minSemanas, buscaPiso]);

  const inputFecha = { padding: '9px 10px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 14 };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Filtros */}
      <div style={{
        background: 'linear-gradient(90deg,#eef2ff 0%, #e0f7fa 50%, #f3e8ff 100%)', border: '1px solid #c7b9ff', padding: 16,
        borderRadius: 12, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14,
      }}>
        <div>
          <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>Desde</label>
          <input type="date" value={filtros.desde} onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })} style={inputFecha} />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>Hasta</label>
          <input type="date" value={filtros.hasta} onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })} style={inputFecha} />
        </div>
        <button onClick={() => aplicar({})} style={{ padding: '10px 18px', background: 'linear-gradient(90deg,#0ea5e9,#7c3aed)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 800, cursor: 'pointer' }}>
          Aplicar fechas
        </button>
        {(filtros.desde || filtros.hasta) && (
          <button onClick={() => aplicar({ desde: '', hasta: '' })} style={{ padding: '10px 14px', background: '#fff7ed', color: '#92400e', border: '1px solid #ffd8a8', borderRadius: 12, fontWeight: 800, cursor: 'pointer' }}>
            🧹 Todo el curso
          </button>
        )}
        <div>
          <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>Comparar con</label>
          <Segmentos valor={filtros.modo} onChange={(v) => aplicar({ modo: v })}
            opciones={[['curso', 'Misma semana del curso'], ['fecha', 'Mismas fechas']]} />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>Rutas</label>
          <Segmentos valor={filtros.rutas} onChange={(v) => aplicar({ rutas: v })}
            opciones={[['1,2', '1 y 2'], ['1,2,3', '1, 2 y 3'], ['todas', 'Todas']]} />
        </div>
      </div>

      {cargando && <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando…</div>}
      {error && !cargando && <div style={{ padding: 14, color: '#b91c1c', background: '#fef2f2', borderRadius: 12 }}>{error}</div>}

      {datos && !cargando && !error && (
        <>
          <div style={{ fontSize: 14, color: '#334155', marginBottom: 12, lineHeight: 1.5 }}>
            Este año <b>{ddmmaa(datos.periodo.actual.desde)} – {ddmmaa(datos.periodo.actual.hasta)}</b> frente a
            {' '}<b>{ddmmaa(datos.periodo.pasado.desde)} – {ddmmaa(datos.periodo.pasado.hasta)}</b>
            {aplicados.modo === 'curso' ? ' (misma semana del curso: la semana 1 es la de la primera compra de cada curso)' : ' (mismos días del año pasado)'}.
            <div style={{ marginTop: 6, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '7px 10px', fontSize: 13 }}>
              📊 Media calculada como en «Gasto semanal por alumno», con las {datos.supervisoras_media.length} supervisoras marcadas para la media
              que también estaban el año pasado: <b>{datos.supervisoras_media.join(', ') || '—'}</b>
              {datos.marcadas_sin_datos_pasado.length > 0 && <> · no cuentan (sin compras el año pasado en estas fechas): {datos.marcadas_sin_datos_pasado.join(', ')}</>}
              {' '}· {datos.semanas_en_media} semanas con datos en los dos años.
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
            <Tarjeta titulo="Gasto semanal por alumno" actual={k.actual.gasto_alumno_semana} pasado={k.pasado.gasto_alumno_semana} fmt={(v) => eur(v)}
              nota="Media de las supervisoras de la media" />
            <Tarjeta titulo="Gasto total" actual={k.actual.gasto_total} pasado={k.pasado.gasto_total} fmt={(v) => eur(v, 0)} />
            <Tarjeta titulo="Ticket medio" actual={k.actual.ticket_medio} pasado={k.pasado.ticket_medio} fmt={(v) => eur(v)} nota="Gasto por compra" />
            <Tarjeta titulo="Compras" actual={k.actual.n_compras} pasado={k.pasado.n_compras} fmt={num} />
            <Tarjeta titulo="Pisos con compras" actual={k.actual.n_pisos} pasado={k.pasado.n_pisos} fmt={num} invertir />
          </div>

          <Seccion titulo="Gasto semanal por alumno" subtitulo="Cada punto es la media de las supervisoras de la media (como en «Gasto semanal por alumno»). Pasa el ratón para ver las fechas y los pisos.">
            <Plot
              data={[
                {
                  x: semanas.map(etiquetaSemana), y: semanas.map((s) => s.pasado), name: 'Año pasado', type: 'scatter', mode: 'lines+markers',
                  line: { color: GRIS, width: 2, dash: 'dot' }, marker: { size: 8, color: GRIS },
                  customdata: semanas.map((s) => [ddmmaa(s.semana_pasado), s.pisos_pasado]),
                  hovertemplate: 'Año pasado (semana del %{customdata[0]}): <b>%{y:.2f} €</b> · %{customdata[1]} pisos<extra></extra>',
                },
                {
                  x: semanas.map(etiquetaSemana), y: semanas.map((s) => s.actual), name: 'Este año', type: 'scatter', mode: 'lines+markers',
                  line: { color: AZUL, width: 2 }, marker: { size: 8, color: AZUL },
                  customdata: semanas.map((s) => [ddmmaa(s.semana), s.pisos_actual]),
                  hovertemplate: 'Este año (semana del %{customdata[0]}): <b>%{y:.2f} €</b> · %{customdata[1]} pisos<extra></extra>',
                },
              ]}
              layout={{
                autosize: true, height: 380, margin: { l: 56, r: 16, t: 10, b: 70 }, hovermode: 'x unified',
                legend: { orientation: 'h', y: 1.1, x: 0 }, paper_bgcolor: '#fff', plot_bgcolor: '#fff',
                yaxis: { title: '€ por alumno', gridcolor: '#eef2f7', zeroline: false, ticksuffix: ' €' },
                xaxis: { gridcolor: '#f8fafc', tickangle: -40 },
              }}
              style={{ width: '100%' }} useResizeHandler config={{ displaylogo: false, responsive: true }}
            />
          </Seccion>

          <Seccion titulo="Gasto por alumno y semana, por etiqueta de producto" subtitulo="En qué tipo de productos ha cambiado el gasto (misma media que arriba).">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10 }}>
              {CATS.map(([c, nombre]) => {
                const a = datos.categorias.actual[c], p = datos.categorias.pasado[c];
                const maxV = Math.max(...CATS.map(([cc]) => Math.max(datos.categorias.actual[cc] || 0, datos.categorias.pasado[cc] || 0)), 1);
                return (
                  <div key={c} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ flex: 1, fontWeight: 900, color: '#0f172a' }}>{nombre}</span>
                      <Delta a={a} b={p} />
                    </div>
                    {[['Este año', a, AZUL], ['Año pasado', p, GRIS]].map(([t, v, col]) => (
                      <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ width: 78, fontSize: 12.5, color: '#64748b' }}>{t}</span>
                        <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 4, height: 12 }}>
                          <div style={{ width: `${((v || 0) / maxV) * 100}%`, background: col, height: 12, borderRadius: 4 }} />
                        </div>
                        <span style={{ width: 66, textAlign: 'right', fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{eur(v)}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </Seccion>

          <Seccion
            titulo="Precios: la misma compra que el año pasado"
            subtitulo={`${num(datos.precios.n_productos)} productos comprados al menos ${datos.precios.min_compras} veces en los dos periodos. Precio medio pagado por unidad.`}
          >
            {datos.precios.indice_pct !== null && (
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 15, color: '#0f172a' }}>
                  Con las mismas cantidades que el año pasado, la compra cuesta hoy{' '}
                  <b style={{ fontSize: 20, color: datos.precios.indice_pct > 0 ? '#b91c1c' : '#15803d' }}>
                    {datos.precios.indice_pct > 0 ? '▲ +' : '▼ '}{String(datos.precios.indice_pct).replace('.', ',')}%
                  </b>
                </div>
                <span style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 999, padding: '3px 10px', fontWeight: 800, fontSize: 13 }}>▲ {datos.precios.suben} suben</span>
                <span style={{ background: '#dcfce7', color: '#15803d', borderRadius: 999, padding: '3px 10px', fontWeight: 800, fontSize: 13 }}>▼ {datos.precios.bajan} bajan</span>
              </div>
            )}
            <div style={{ marginBottom: 10 }}>
              <Segmentos valor={vistaPrecios} onChange={setVistaPrecios}
                opciones={[['mas_comprados', 'Los más comprados'], ['mayores_subidas', 'Los que más suben'], ['mayores_bajadas', 'Los que más bajan']]} />
            </div>
            <Tabla
              key={vistaPrecios}
              filas={datos.precios[vistaPrecios]}
              ordenInicial={vistaPrecios === 'mas_comprados' ? { k: 'unidades_pasado', asc: false } : { k: 'dif_pct', asc: vistaPrecios === 'mayores_bajadas' }}
              columnas={[
                { k: 'nombre', t: 'Producto', ascPrimero: true },
                { k: 'precio_pasado', t: 'Año pasado', num: true, r: (f) => eur(f.precio_pasado) },
                { k: 'precio_actual', t: 'Ahora', num: true, r: (f) => eur(f.precio_actual) },
                { k: 'dif_pct', t: 'Cambio', num: true, r: (f) => <Delta a={f.precio_actual} b={f.precio_pasado} /> },
                { k: 'unidades_pasado', t: 'Uds. año pasado', num: true, r: (f) => num(f.unidades_pasado) },
              ]}
            />
          </Seccion>

          <Seccion titulo="Por supervisora" subtitulo="Supervisora que hizo cada compra (las de este año y las del año pasado pueden no coincidir).">
            <Tabla
              filas={datos.supervisoras}
              ordenInicial={{ k: 'supervisora', asc: true }}
              columnas={[
                { k: 'supervisora', t: 'Supervisora', ascPrimero: true, r: (f) => <>{f.supervisora}{f.en_media && <span style={{ marginLeft: 6, background: '#eef2ff', color: '#3730a3', borderRadius: 999, padding: '1px 7px', fontSize: 11, fontWeight: 800 }}>en la media</span>}</> },
                { k: 'pasado', t: 'Año pasado', num: true, r: (f) => <>{eur(f.pasado)} <span style={{ color: '#94a3b8', fontSize: 12 }}>{f.pisos_pasado ? `· ${f.pisos_pasado} pisos` : ''}</span></> },
                { k: 'actual', t: 'Este año', num: true, r: (f) => <>{eur(f.actual)} <span style={{ color: '#94a3b8', fontSize: 12 }}>{f.pisos_actual ? `· ${f.pisos_actual} pisos` : ''}</span></> },
                { k: 'cambio', t: 'Cambio', num: true, orden: (f) => pct(f.actual, f.pasado), r: (f) => <Delta a={f.actual} b={f.pasado} /> },
              ]}
            />
          </Seccion>

          <Seccion
            titulo="Por piso"
            subtitulo="El mismo piso en los dos años. Gasto semanal por alumno medio de las semanas en que compró."
            derecha={(
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input placeholder="Buscar piso o supervisora" value={buscaPiso} onChange={(e) => setBuscaPiso(e.target.value)}
                  style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 14, minWidth: 200 }} />
                <label style={{ fontSize: 13, color: '#475569' }}>
                  Mín. semanas con compra{' '}
                  <select value={minSemanas} onChange={(e) => setMinSemanas(Number(e.target.value))} style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #cbd5e1' }}>
                    {[1, 2, 3, 4, 6, 8].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              </div>
            )}
          >
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>{pisos.length} pisos</div>
            <Tabla
              filas={pisos}
              ordenInicial={{ k: 'dif', asc: false }}
              max={todosPisos ? null : 40}
              columnas={[
                { k: 'abreviatura', t: 'Piso', ascPrimero: true },
                { k: 'supervisora', t: 'Supervisora ahora', ascPrimero: true },
                { k: 'pasado', t: 'Año pasado', num: true, r: (f) => <>{eur(f.pasado)} <span style={{ color: '#94a3b8', fontSize: 12 }}>· {f.semanas_pasado} sem</span></> },
                { k: 'actual', t: 'Este año', num: true, r: (f) => <>{eur(f.actual)} <span style={{ color: '#94a3b8', fontSize: 12 }}>· {f.semanas_actual} sem</span></> },
                { k: 'dif', t: 'Diferencia', num: true, r: (f) => <>{f.dif > 0 ? '+' : ''}{eur(f.dif)} <Delta a={f.actual} b={f.pasado} /></> },
              ]}
            />
            {pisos.length > 40 && (
              <button onClick={() => setTodosPisos((v) => !v)} style={{ marginTop: 10, border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 10, padding: '8px 14px', fontWeight: 800, cursor: 'pointer' }}>
                {todosPisos ? 'Ver solo 40' : `Ver los ${pisos.length} pisos`}
              </button>
            )}
          </Seccion>
        </>
      )}
    </div>
  );
}
